import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { mensajeErrorFiscal } from "services/facturacionErrores";
import {
  Autocomplete,
  Card,
  CircularProgress,
  Collapse,
  createFilterOptions,
  Divider,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { truncar } from "utils/formatters";
import { valorAPesos } from "utils/formatters";
import { validateCUIL } from "utils/validations";
import dataPagos from "../data/dataPagos";
import DataTable from "components/DataTable";
import SinDatos from "components/Carteles/SinDatos";
import { MessageManager } from "context";
import dayjs from "dayjs";
// import { printFactura } from "utils/comanda";
import SoftBox from "components/CheckBox";
import { getUproColor } from "utils/colors";
import { printTicket } from "utils/comanda";
import { SEARCH_UNIFIED_USERS } from "services/unifiedUsers";

const isClienteAlumnoAcademica = (cliente = {}) =>
  String(cliente?.appUsuario || "").trim() === "Alumnos" &&
  String(cliente?.tipoUsuario || "").trim() === "Alumno";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "95%", sm: "90%", xxl: "60%" },
  overflowY: "auto",
  p: 3,
};

const facturaInicial = {
  cliente: "Consumidor final",
  tipoFactura: "B",
  nro_doc: "",
};
const tiposComprobantes = {
  A: 1,
  B: 6,
  C: 11,
};
const clienteInicial = {
  dni: "",
  nombre: "",
  apellido: "",
};

const ModalFacturar = ({
  open,
  handleClose,
  total,
  cerrarVenta,
  pedido,
  mesa,
  idPedido,
  setPedido,
}) => {
  const { handleSnackbar } = useContext(MessageManager);
  const [factura, setFactura] = useState(facturaInicial);
  const [cliente, setCliente] = useState(clienteInicial);
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncing, setDebouncing] = useState(false);
  const [loadingPrint, setLoadingPrint] = useState(false);

  const [pagos, setPagos] = useState([]);
  const [rows, setRows] = useState([]);
  const { columns, getRows } = dataPagos;
  const [medioDePago, setMedioDePago] = useState({
    nombre: "",
    totalPago: 0,
    recargo: 0,
    cuotas: 1,
    alias: "",
    cbu: "",
    titular: "",
  });

  const recargoPago = truncar(medioDePago.totalPago * (medioDePago.recargo / 100)) || 0;
  const totalPagos = useMemo(() => pagos.reduce((acc, pago) => acc + pago.totalPago, 0), [pagos]);
  const totalRecargo = useMemo(
    () => pagos.reduce((acc, pago) => acc + pago.totalRecargo, 0),
    [pagos]
  );
  const descuento = pagos
    .filter((pago) => pago.nombre === "Descuento")
    .reduce((acc, pago) => acc + pago.totalPago, 0);
  const restanteAPagar = total - totalPagos;
  const calculoTotalFinal = totalPagos + totalRecargo - descuento;
  const totalFinal = truncar(calculoTotalFinal) || 0;

  useEffect(() => {
    setMedioDePago((prev) => ({
      ...prev,
      totalPago: restanteAPagar,
    }));
  }, [restanteAPagar]);

  const { data } = useQuery(
    gql`
      query getMediosPago($filter: JSON) {
        mediosDePago(filter: $filter) {
          id
          nombre
          cuotas
          recargo
          favorito
          titular
          alias
          cbu
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      onCompleted: (data) => {
        const medioDePagoFavorito = data?.mediosDePago.filter((medio) => medio.favorito);
        if (medioDePagoFavorito.length > 0) {
          setMedioDePago({
            ...medioDePagoFavorito[0],
            totalPago: restanteAPagar,
            id: medioDePagoFavorito[0].id,
          });
        }
      },
    }
  );
  const handleChangeFormaDePago = (e) => {
    if (e.target.name === "nombre") {
      const selectedId = e.target.value;
      const selectedMedio = data?.mediosDePago.find((medio) => medio.id == selectedId) || {
        nombre: e.target.value === "Cuenta corriente" ? "Cuenta corriente" : "Descuento",
        recargo: 0,
        cuotas: 1,
        id: e.target.value === "Cuenta corriente" ? "Cuenta corriente" : 0,
        alias: "",
        cbu: "",
        titular: "",
      };
      setMedioDePago((prev) => ({
        ...prev,
        nombre: selectedMedio?.nombre,
        recargo: selectedMedio?.recargo,
        cuotas: selectedMedio?.cuotas,
        alias: selectedMedio?.alias,
        cbu: selectedMedio?.cbu,
        titular: selectedMedio?.titular,
        id: selectedMedio?.id,
      }));
    } else {
      setMedioDePago({
        ...medioDePago,
        [e.target.name]: e.target.value,
      });
    }
  };
  const validarCuit = () => {
    // Si hay un cliente seleccionado o es modo Particular con tipo B, siempre es válido
    if (cliente?.id || factura.tipoFactura === "B") {
      return true;
    }
    // Para otros tipos de factura sin cliente, validar el DNI
    if (factura.tipoFactura === "A") {
      return validateCUIL(factura.nro_doc || "");
    }
    if (factura.tipoFactura === "C") {
      return (
        factura.nro_doc.length === 8 || factura.nro_doc.length === 7 || factura.nro_doc.length === 0
      );
    }
    return !!factura.tipoFactura;
  };
  useEffect(() => {
    setRows(getRows(pagos, ejecutarAccion));
  }, [pagos]);

  //Buscar en la tablaClientes por dni y app
  const [getClientesUpro, { data: dataClientes }] = useLazyQuery(
    gql`
      query clientes($filter: JSON) {
        clientes(filter: $filter) {
          id
          idUsuario
          appUsuario
          tipoUsuario
          habilitadoCuentaCorriente
        }
      }
    `
  );

  //Facturar
  const [saveVentaMesa, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveVentaMesa($input: VentaInput!) {
        saveVentaMesa(input: $input) {
          id
          idPedido
          mesa
          cliente {
            id
            nombre
            apellido
          }
          tipoFactura
          fechaFactura
          tipo_cbte
          tipo_doc
          nro_doc
          imp_iva21
          subtotal21
          concepto
          nroAnulado
          numeroDeFacturaAfip
          CAE
          CAEFchVto
          total
          url
          pto_vta
          descuento
          recargo
          idAnulada
          appUsuario
          pedido {
            venta {
              id
            }
            detalles {
              producto {
                id
                nombre
                seccion
              }
              cantidad
              numeroComanda
            }
          }
          pagos {
            id
            nombre
            totalPago
            cantidad
            medioDePago {
              nombre
              recargo
            }
            concepto
            fechaPago
          }
        }
      }
    `
  );
  const clienteSeleccionado = dataClientes?.clientes?.[0] || null;
  const cuentaCorrienteRestringidaPorPerfil = isClienteAlumnoAcademica(clienteSeleccionado);
  const handleSave = () => {
    const detalles = pedido.detalles
      .filter((detalle) => detalle.estado !== "CANCELADO")
      .map((detalle) => ({
        idProducto: detalle.producto.id,
        comentario: detalle.comentario,
        cantidad: Number(detalle.cantidad),
        precio: detalle.precio,
      }));

    let tipo_cbte = tiposComprobantes[factura.tipoFactura];
    let subtotal21 = totalFinal;
    let nro_doc = factura.nro_doc || "0";
    let tipo_doc = factura?.nro_doc.length == 8 ? 96 : 99;
    let cuentaCorriente = pagos.some((pago) => pago.nombre === "Cuenta corriente");
    saveVentaMesa({
      variables: {
        input: {
          idPedido: idPedido,
          nombre: cliente?.nombre || "",
          apellido: cliente?.apellido || "",
          cliente: (cliente.nombre || "") + " " + (cliente.apellido || ""),
          tipoFactura: factura.tipoFactura,
          tipo_cbte: tipo_cbte,
          tipo_doc: tipo_doc,
          nro_doc: nro_doc,
          subtotal21: subtotal21,
          imp_iva21: 0,
          concepto: 1,
          nroAnulado: 0,
          total: totalFinal,
          fecha: dayjs().format("YYYY-MM-DD"),
          cuitEmpresa: null,
          pto_vta: 3,
          pagos: !cuentaCorriente ? pagos : [],
          descuento: descuento,
          recargo: totalRecargo,
          appUsuario: cliente?.tipoApp || "Particular",
          idUsuario: cliente?.id || cliente?.user?.id,
          tipoUsuario: cliente.tipoUser || cliente?.tipoUsuario,
          detalles: detalles,
          cuentaCorriente: cuentaCorriente,
          estado: "CERRADO",
          mesa: mesa,
        },
      },
    })
      .then((res) => {
        // printFactura(data.data.saveVentaMesa, user.empresa);
        // Separar los items por sección
        const detallesNoCancelados = pedido.detalles.filter(
          (detalle) => detalle.estado !== "CANCELADO"
        );
        const secciones = [
          ...new Set(detallesNoCancelados.map((detalle) => detalle.producto.seccion)),
        ];
        const dataAfip = {
          id: res.data.saveVentaMesa.pedido.venta.id,
          cliente: res.data?.saveVentaMesa?.cliente?.nombre
            ? "Consumidor final"
            : res.data?.saveVentaMesa?.cliente?.nombre +
              " " +
              res.data?.saveVentaMesa?.cliente?.apellido,
          fecha: dayjs().format("YYYY-MM-DD"),
          idPedido: res.data.saveVentaMesa.idPedido,
          tipoFactura: "B",
          url: res.data.saveVentaMesa.url,
          cae: res.data.saveVentaMesa.CAE,
          vencimientoCae: res.data.saveVentaMesa.CAEFchVto,
          numeroFacturaAfip: res.data.saveVentaMesa.numeroDeFacturaAfip,
          descuento: res.data.saveVentaMesa.descuento,
          recargo: res.data.saveVentaMesa.recargo,
          total: res.data.saveVentaMesa.total,
        };
        const comandasPorDetalle = (res.data.saveVentaMesa.pedido?.detalles || []).reduce(
          (acc, detalle) => {
            const key = `${detalle.producto.id}-${Number(detalle.cantidad)}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(detalle.numeroComanda);
            return acc;
          },
          {}
        );
        const detallesPorSeccion = secciones.map((seccion) => {
          return {
            seccion: seccion,
            detalles: detallesNoCancelados
              .filter((detalle) => detalle.producto.seccion === seccion)
              .map((detalle) => {
                const key = `${detalle.producto.id}-${Number(detalle.cantidad)}`;
                const nroComanda =
                  comandasPorDetalle[key]?.shift() ||
                  res.data.saveVentaMesa.pedido?.detalles?.find(
                    (d) => d.producto.id == detalle.producto.id
                  )?.numeroComanda;
                return {
                  ...detalle,
                  precio: Number(detalle.precio),
                  nroComanda: nroComanda,
                };
              }),
          };
        });
        // Enviar a imprimir la comanda por seccion
        setLoadingPrint(true);
        detallesPorSeccion.forEach(async (seccion) => {
          await printTicket(dataAfip, seccion.detalles).then((response) => {
            if (response?.status !== 200) {
              handleSnackbar(
                `Error al imprimir comanda de ${seccion.seccion} ${
                  response?.data?.message ? `: ${response?.data?.message}` : ""
                }`,
                "error"
              );
            }
          });
        });
        setLoadingPrint(false);
        handleSnackbar("Finalizado correctamente", "success");
        setPedido({
          mozo: "",
          detalles: [],
        });
        handleClose();
        cerrarVenta();
      })
      .catch((err) => {
        handleSnackbar(mensajeErrorFiscal(err), "error");
      });
  };

  const ejecutarAccion = (accion, item) => {
    switch (accion) {
      case "borrar":
        setPagos((prev) =>
          prev.filter(
            (pago) =>
              !(
                pago.totalPago === item.totalPago &&
                pago.nombre === item.nombre &&
                pago.cuotas === item.cuotas
              )
          )
        );
        break;
      default:
        break;
    }
  };

  const cerrarModal = () => {
    if (loadingSave) return;
    let medioPagoFavorito = data?.mediosDePago.find((medio) => medio.favorito);
    if (medioPagoFavorito) {
      setMedioDePago({
        ...medioPagoFavorito,
        totalPago: restanteAPagar,
      });
    } else {
      setMedioDePago({
        nombre: "",
        totalPago: 0,
        recargo: 0,
        cuotas: 1,
        alias: "",
        cbu: "",
        titular: "",
      });
    }
    setFactura({
      ...facturaInicial,
      tipoFactura: "B", // Resetear a Particular con tipo B
    });
    setCliente(clienteInicial);
    setPagos([]);
    setSearch("");
    handleClose();
  };
  const filterOptions = createFilterOptions({
    stringify: ({ apellido, nombre, dni, email }) => {
      const searchableText = `${apellido || ""} ${nombre || ""} ${dni || ""} ${email || ""}`;
      return searchableText;
    },
    limit: 15,
  });

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };
  const [searchUnifiedUsers] = useLazyQuery(SEARCH_UNIFIED_USERS, {
    fetchPolicy: "network-only",
  });
  // Función unificada de búsqueda en todas las fuentes
  const handleSearchUnified = useCallback(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setClientes([]);
        setDebouncing(false);
        return;
      }

      try {
        setDebouncing(true);

        const { data } = await searchUnifiedUsers({ variables: { searchTerm } });
        const allUsers = (data?.searchUnifiedUsers || []).map((user) => ({
          ...user,
          displayText: `${user.nombre || ""} ${user.apellido || ""}${
            user.dni ? ` - DNI: ${user.dni}` : ""
          }${user.email ? ` - Email: ${user.email}` : ""} (${user.tipoApp || "Comedor"})`,
        }));

        setClientes(allUsers);
        setDebouncing(false);
      } catch (error) {
        console.error("Error en búsqueda unificada:", error);
        setClientes([]);
        setDebouncing(false);
      }
    }, 1000),
    [searchUnifiedUsers]
  );
  useEffect(() => {
    if (open && search) {
      handleSearchUnified(search);
    }
  }, [search, open, handleSearchUnified]);
  const handleSearchChange = (e, value) => {
    if (value.length > 1) {
      setDebouncing(true);
    }
    setSearch(value);
  };

  //SI se selecciono un nuevo cliente, realizar la query getClientesUpro para obtener sus datos
  useEffect(() => {
    if (cliente) {
      getClientesUpro({
        variables: {
          filter: {
            dni: cliente.dni || "",
            appUsuario: cliente.tipoApp || "Particular",
          },
        },
      });
    }
  }, [cliente]);
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      closeAfterTransition
      disableEscapeKeyDown={loadingSave ? true : false}
      onClose={cerrarModal}
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox display="flex" flexDirection="column" gap={2}>
            <SoftBox display="flex" justifyContent="space-between">
              <Grid display="flex" flexDirection="row">
                <SoftTypography fontWeight="bold">Total: {valorAPesos(total)}</SoftTypography>
              </Grid>
              <Grid>
                <Tooltip title="Cerrar" placement="top">
                  <Icon fontSize="medium" onClick={cerrarModal} sx={{ cursor: "pointer" }}>
                    close
                  </Icon>
                </Tooltip>
              </Grid>
            </SoftBox>
            <SoftBox>
              <InputLabel htmlFor="cliente">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Buscar Usuario por DNI/Nombre
                  <SoftTypography variant="caption" fontWeight="bold" color="secondary">
                    &nbsp;(Si no encuentra, continúa como Particular)
                  </SoftTypography>
                </SoftTypography>
              </InputLabel>
              <Autocomplete
                clearOnEscape
                options={!debouncing ? clientes : []}
                value={cliente ? cliente : null}
                loading={debouncing}
                loadingText="Buscando en todas las bases..."
                noOptionsText={
                  search.length > 1 ? "No se encontraron resultados" : "Escriba para buscar"
                }
                onInputChange={handleSearchChange}
                getOptionLabel={(option) =>
                  option?.displayText ||
                  (option?.nombre
                    ? `${option.nombre} ${option.apellido}${
                        option.dni ? ` - DNI: ${option.dni}` : ""
                      }${option.email ? ` - Email: ${option.email}` : ""}`
                    : "")
                }
                filterOptions={(options, params) => {
                  const filtered = filterOptions(options, params);
                  return filtered;
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setCliente(newValue);
                    setFactura((prev) => ({
                      ...prev,
                      nro_doc: newValue.dni || "",
                      tipoFactura: "B", // Siempre tipo B para usuarios registrados
                    }));
                  } else {
                    setCliente(clienteInicial);
                    setFactura((prev) => ({
                      ...prev,
                      nro_doc: "",
                      tipoFactura: "B", // Tipo B para Particular
                    }));
                  }
                }}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      slotprops={{
                        input: {
                          ...params.InputProps,
                          type: "search",
                        },
                      }}
                    />
                  );
                }}
              />
            </SoftBox>
            {/* <Collapse in={!cliente || !cliente.id} mountOnEnter unmountOnExit>
              <SoftBox>
                <InputLabel htmlFor="handleChangeFactura">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de factura
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="tipoFactura"
                  value={factura?.tipoFactura}
                  onChange={handleChangeFactura}
                >
                  {TIPO_FACTURAS.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </SoftBox>
            </Collapse> */}
            <Divider />
            <SoftBox
              display="flex"
              flexDirection={{
                xs: "column",
                sm: "row",
              }}
              gap={2}
            >
              <SoftBox
                sx={{
                  width: "100%",
                }}
              >
                <InputLabel htmlFor="condicionIva">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Medio de pago
                  </SoftTypography>
                </InputLabel>
                <Select name="nombre" value={medioDePago?.id} onChange={handleChangeFormaDePago}>
                  {clienteSeleccionado?.habilitadoCuentaCorriente &&
                    !cuentaCorrienteRestringidaPorPerfil && (
                    <MenuItem disabled={pagos.length > 0} value={"Cuenta corriente"}>
                      Cuenta corriente
                    </MenuItem>
                  )}
                  {/* <MenuItem value={7}>Efectivo</MenuItem> */}
                  <MenuItem value={1}>Transferencia</MenuItem>
                </Select>
              </SoftBox>
              <SoftBox>
                <InputLabel htmlFor="Total">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {medioDePago.id === 0 ? "Descuento $" : "Total a pagar"}
                  </SoftTypography>
                  <SoftTypography component="label" variant="caption" color="primary">
                    {medioDePago.id !== 0 && ` (Recargo: $${recargoPago})`}
                  </SoftTypography>
                </InputLabel>
                <SoftInputNumber
                  placeholder="$"
                  name="totalPago"
                  value={medioDePago.totalPago}
                  onChange={handleChangeFormaDePago}
                  disabled={medioDePago.id === "Cuenta corriente"}
                />
              </SoftBox>
              <SoftBox>
                <InputLabel htmlFor="Total">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    &nbsp;
                  </SoftTypography>
                </InputLabel>
                <SoftButton
                  fullWidth
                  color="primary"
                  circular
                  onClick={() => {
                    setPagos((prev) => [
                      ...prev,
                      {
                        nombre: medioDePago.nombre,
                        cuotas: medioDePago.cuotas,
                        totalPago: medioDePago.totalPago,
                        recargo: recargoPago,
                        totalRecargo: recargoPago,
                        idMedioDePago: medioDePago.id,
                      },
                    ]);
                  }}
                  disabled={medioDePago.totalPago <= 0 || !medioDePago.nombre}
                >
                  Agregar&nbsp;
                  <Icon>add</Icon>
                </SoftButton>
              </SoftBox>
            </SoftBox>
            <Collapse in={medioDePago?.alias || medioDePago?.cbu} mountOnEnter unmountOnExit>
              <Card>
                <SoftBox p={2} display="flex" flexDirection="column" gap={2}>
                  <SoftBox display="flex" gap={2} alignItems="center">
                    <Icon color="primary">account_balance</Icon>
                    <SoftTypography variant="h6">Datos de la cuenta</SoftTypography>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    gap={2}
                    flexDirection={{
                      xs: "column",
                      sm: "row",
                    }}
                  >
                    {medioDePago?.alias && (
                      <SoftBox sx={{ width: "100%" }}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Alias
                        </SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold">
                          {medioDePago?.alias}
                        </SoftTypography>
                      </SoftBox>
                    )}
                    {medioDePago?.cbu && (
                      <SoftBox sx={{ width: "100%" }}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          CBU
                        </SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold">
                          {medioDePago?.cbu}
                        </SoftTypography>
                      </SoftBox>
                    )}
                  </SoftBox>
                </SoftBox>
              </Card>
            </Collapse>
            <SoftBox>
              <Card
                sx={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  zIndex: 2,
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                }}
              >
                {pagos.length > 0 ? (
                  <DataTable columns={columns} rows={rows} />
                ) : (
                  <SinDatos
                    titulo="No hay pagos"
                    mensaje="Cargue un pago para finalizar"
                    icono="payments"
                  />
                )}
              </Card>
              <Card
                sx={{
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  position: "sticky",
                  marginTop: -2,
                  zIndex: 1,
                }}
              >
                <SoftBox bgColor="primary">
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    bgColor={
                      restanteAPagar === 0
                        ? getUproColor("uproGreen")
                        : totalFinal === 0 || restanteAPagar < 0
                        ? getUproColor("uproPink")
                        : getUproColor("uproYellow")
                    }
                    p={2}
                    pt={4}
                  >
                    <SoftTypography variant="h6" fontWeight="regular" color="white">
                      Restante: {valorAPesos(restanteAPagar)}
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold" color="white">
                      Total: {valorAPesos(totalFinal)}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Card>
            </SoftBox>
          </SoftBox>
          <Grid
            display={{
              xs: "flex-row",
              lg: "flex",
            }}
            justifyContent="flex-end"
            alignItems="center"
            mt={{
              xs: 0,
              sm: 2,
            }}
          >
            <Grid
              mr={{
                xs: 0,
                lg: 2,
              }}
              mt={{
                xs: 2,
                lg: 0,
              }}
            >
              <SoftButton
                color="primary"
                circular
                onClick={cerrarModal}
                fullWidth={{
                  xs: true,
                  lg: false,
                }}
                disabled={loadingSave || loadingPrint}
              >
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
            </Grid>
            <Grid
              mt={{
                xs: 2,
                lg: 0,
              }}
            >
              <SoftButton
                color="uproGreen"
                circular
                fullWidth={{
                  xs: true,
                  lg: false,
                }}
                onClick={handleSave}
                disabled={
                  pagos.length === 0 ||
                  loadingSave ||
                  loadingPrint ||
                  !validarCuit() ||
                  restanteAPagar !== 0
                }
              >
                {loadingSave || loadingPrint ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>attach_money</Icon>
                )}
                &nbsp;Finalizar
              </SoftButton>
            </Grid>
          </Grid>
        </Card>
      </Fade>
    </Modal>
  );
};

export default ModalFacturar;

ModalFacturar.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  total: PropTypes.number,
  cerrarVenta: PropTypes.func,
  pedido: PropTypes.object,
  mesa: PropTypes.string,
  idPedido: PropTypes.string,
  setPedido: PropTypes.func,
};
